import { useMemo } from "react";

import { LoopRouteResponseExpanded } from "@bridgesplit/abf-react";
import { OracleQuote } from "@bridgesplit/abf-sdk";

import { ExecutionDetailsLabels, LoopExecutionDetails } from "./types";

const BASE_EXECUTION_DETAILS_LABELS: Readonly<Required<ExecutionDetailsLabels>> = {
    executionImpact: { caption: "Execution impact", tooltip: undefined },
    fairMarketValue: {
        caption: "Fair market value",
        tooltip:
            "What your position is worth based on oracle prices - used for PnL before accounting for execution impact"
    },
    fees: { caption: "Fees", tooltip: "Swap fees charged by AMMs" },
    priceImpact: { caption: "Price impact", tooltip: "Execution price move due to trade size and pool depth" },
    slippage: {
        caption: "Slippage",
        tooltip: "Difference between expected and actual execution price within your slippage tolerance"
    },
    swapDifference: {
        caption: "Swap difference",
        tooltip: undefined
    },
    receiveOnClose: { caption: "Receive on close", tooltip: undefined }
};

export function useExecutionDetailsLabels({
    overrides
}: {
    overrides?: Partial<ExecutionDetailsLabels>;
}): Required<ExecutionDetailsLabels> {
    return useMemo(() => {
        const merged: Required<ExecutionDetailsLabels> = JSON.parse(JSON.stringify(BASE_EXECUTION_DETAILS_LABELS));

        for (const k of Object.keys(overrides || {}) as (keyof ExecutionDetailsLabels)[]) {
            const v = (overrides || {})[k];
            if (v) merged[k] = { ...merged[k], ...v };
        }

        return merged;
    }, [overrides]);
}

export function useExecutionDetails({
    principalOracle,
    slippagePercentDecimals,
    externalQuote,
    receiveUsdAmount,
    leftOverPrincipal,
    fairMarketValue
}: {
    principalOracle: OracleQuote | undefined;
    slippagePercentDecimals: number | undefined;
    externalQuote: LoopRouteResponseExpanded | undefined;
    receiveUsdAmount: number | undefined;
    leftOverPrincipal: number | undefined;
    fairMarketValue: number | undefined;
}): LoopExecutionDetails | undefined {
    // Calculate execution loss details
    const executionDetails = useMemo(() => {
        if (!principalOracle || fairMarketValue === undefined) return undefined;

        // Calculate fee impact
        const feeAmount = externalQuote?.feeDecimals ? externalQuote.feeDecimals * (receiveUsdAmount ?? 0) : 0;

        // Calculate price impact
        const priceImpactAmount = externalQuote?.priceImpactDecimals
            ? externalQuote.priceImpactDecimals * (receiveUsdAmount ?? 0)
            : 0;

        const leftOverPrincipalUsd = leftOverPrincipal ? principalOracle.getUsdAmount(leftOverPrincipal) : 0;

        // Calculate slippage
        const slippageAmount = slippagePercentDecimals ? slippagePercentDecimals * fairMarketValue : 0;

        const oraclePriceDeviation =
            fairMarketValue - (receiveUsdAmount ?? 0) - feeAmount - priceImpactAmount - slippageAmount;

        const executionLossAmount = (receiveUsdAmount ?? 0) - fairMarketValue;

        return {
            receiveUsdAmount: receiveUsdAmount ?? 0,
            executionLossAmount,
            feeAmount,
            priceImpactAmount,
            slippageAmount,
            fairMarketValue,
            oraclePriceDeviation,
            leftOverPrincipalUsd
        };
    }, [principalOracle, slippagePercentDecimals, fairMarketValue, receiveUsdAmount, leftOverPrincipal, externalQuote]);

    return executionDetails;
}
